import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  Alert,
  CardBody,
  Button,
  Form,
} from "reactstrap";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { fetchProfile, editUser } from "../../slices/thunks";
import { resetProfileFlag } from "../../slices/auth/profile/reducer";
import NameInput from "../../common/NameInput";
import EmailInput from "../../common/EmailInput";
import PasswordInput from "../../common/PasswordInput";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const STATIC_ADMIN_ID = "1262";

const UserProfile = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.Profile.user);
  const [formValues, setFormValues] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchProfile());

    return () => {
      dispatch(resetProfileFlag());
    };
  }, [dispatch]);

  useEffect(() => {
    if (user?.data?.[0]) {
      const admin = user.data[0];
      setFormValues({
        firstName: admin.name || "",
        email: admin.email || "",
        password: "",
      });
    }
  }, [user]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: formValues || {
      firstName: "",
      email: "",
      password: "",
    },
    validate: (values) => {
      const errors = {};
      if (!values.firstName) {
        errors.firstName = "Name is required";
      }
      if (!values.email) {
        errors.email = "Email is required";
      }
      if (values.password && values.password.length < 6) {
        errors.password = "Password must be at least 6 characters";
      }
      return errors;
    },
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const payload = {
          id: STATIC_ADMIN_ID,
          data: {
            name: values.firstName,
            email: values.email,
          },
        };

        if (values.password && values.password.trim() !== "") {
          payload.data.password = values.password;
        }

        const response = await dispatch(editUser(payload));

        if (response.payload) {
          toast.success("Profile updated successfully!", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          await dispatch(fetchProfile());
          validation.setFieldValue("password", "");
        } else {
          toast.error("Failed to update profile. Please try again.", {
            position: "top-right",
            autoClose: 2000,
          });
        }
      } catch (error) {
        toast.error("An error occurred. Please try again.", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  if (!formValues) return <Alert color="info">Loading profile...</Alert>;

  return (
    <React.Fragment>
      {/* <ToastContainer /> */}
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Edit Profile" pageTitle="Edit Profile" />
        </Container>
      </div>

      <div className="mt-lg-5">
        <Card>
          <CardBody>
            <Form onSubmit={validation.handleSubmit}>
              <NameInput
                name="firstName"
                id="firstName"
                label="Name"
                required={true}
                value={validation.values.firstName}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                error={
                  validation.touched.firstName &&
                  validation.errors.firstName
                }
              />
              <br />

              <EmailInput
                name="email"
                id="email"
                required={true}
                layout="below"
                disabled={true}
                value={validation.values.email}
              />
              <br />

              <PasswordInput
                name="password"
                id="password"
                required={false}
                layout="below"
                placeholder="Enter new password"
                helpText="Leave blank to keep current password."
                value={validation.values.password}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                error={
                  validation.touched.password &&
                  validation.errors.password
                }
              />

              <div className="text-center mt-4">
                <Button
                  type="submit"
                  style={{ backgroundColor: "#5962ef" }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "Update Profile"}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </div>
    </React.Fragment>
  );
};

export default UserProfile;
